import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Sparkles, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { DAYS, isTime, serializeWeek, weekSummary } from "@/lib/hours";

const DAY_KEYS = DAYS.map((d) => d.key);

const RESPONSE_SCHEMA = {
  type: "object",
  properties: {
    open_days: {
      type: "array",
      description: "One entry per day the business is open. A day left out is closed.",
      items: {
        type: "object",
        properties: {
          day: { type: "string", enum: DAY_KEYS },
          open: { type: "string", description: "Opening time, 24-hour HH:MM" },
          close: {
            type: "string",
            description: "Closing time, 24-hour HH:MM — earlier than the opening time when the night runs past midnight",
          },
        },
        required: ["day", "open", "close"],
      },
    },
    line: { type: "string", description: "The same week as one short line, in the language it was written in" },
  },
  required: ["open_days"],
};

const PROMPT = `Turn a Puerto Rico business's opening hours into a weekly schedule.

The hours arrive written any way at all, in English or Spanish: "Tue-Sun 11am-9pm, closed Mondays", "todos los días de 9 a 5", "abrimos de mediodía a medianoche", "weekdays 8-4, weekends by appointment", "abierto hasta tarde".

Rules:
- one entry per day the business is open, each with day as mon, tue, wed, thu, fri, sat, sun
- open and close as 24-hour HH:MM
- a closing time after midnight is written earlier than the opening time (a bar open 8pm to 2am is open 20:00, close 02:00)
- a day that is closed, or that the text never mentions, is left out of open_days entirely
- line: the week in one short line for a visitor, written in the language the hours were written in (e.g. "Mar–Dom 11am–9pm")`;

// The model reads times its own way — "11am", "11:00 AM", "9" — and every one
// of them has to land as the canonical 24-hour HH:MM the week stores.
function toTime(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "");
  if (isTime(raw)) return raw;
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!match) return "";
  let hour = Number(match[1]);
  const mins = match[2] || "00";
  if (match[3] === "pm" && hour < 12) hour += 12;
  if (match[3] === "am" && hour === 12) hour = 0;
  if (hour > 23) return "";
  return `${String(hour).padStart(2, "0")}:${mins}`;
}

/**
 * Hours out of a sentence. The week is written the way the person already says
 * it out loud, the model reads it into the stored schedule, and the day rows
 * above show the result — still editable one day at a time, which is the point:
 * this fills the week in, it does not lock it.
 */
export default function HoursAIFill({ inputCls = "", onApply }) {
  const { lang, t } = useLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `${PROMPT}\n\nHours as the business writes them:\n"""${text.trim()}"""`,
        response_json_schema: RESPONSE_SCHEMA,
      });
      const week = Object.fromEntries(DAY_KEYS.map((key) => [key, null]));
      (result?.open_days || []).forEach((entry) => {
        const key = DAY_KEYS.includes(entry?.day) ? entry.day : "";
        const open = toTime(entry?.open);
        const close = toTime(entry?.close);
        if (key && open && close) week[key] = { open, close };
      });
      if (!Object.values(week).some(Boolean)) throw new Error("no days read");
      onApply({
        hours: serializeWeek(week),
        line: String(result?.line || "").trim() || weekSummary(week, lang, t("hours.daily")),
      });
      setText("");
    } catch {
      setErr(t("hours.aiError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-[12px] border border-[#deded9] bg-[#fafaf7] p-3">
      <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-[#6d6d6a]">
        <Sparkles className="w-3.5 h-3.5" /> {t("hours.aiTitle")}
      </div>
      <p className="mt-1 text-[11.5px] text-[#8d8d88] leading-[1.45]">{t("hours.aiHint")}</p>
      <textarea
        rows={2}
        className={`${inputCls} mt-2`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("hours.aiPlaceholder")}
      />
      {err && <div className="mt-1.5 text-[11.5px] font-bold text-red-600">{err}</div>}
      <button
        type="button"
        onClick={run}
        disabled={busy || !text.trim()}
        className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#171717] px-4 py-2 text-[12.5px] font-bold text-white disabled:opacity-50 sm:w-auto"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {busy ? t("hours.aiWorking") : t("hours.aiButton")}
      </button>
    </div>
  );
}